import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Loader2, X } from 'lucide-react';
import { useDeactivationTracker } from '../../../../contexts/deactivation/DeactivationTrackerContext';
import {
  DEACTIVATION_STEP_ORDER,
  type DeactivationStatusResponse,
} from '../../../../api/organizationStatus';

const completedStepCount = (status: DeactivationStatusResponse | undefined): number => {
  if (!status?.steps) return 0;
  return Object.values(status.steps).filter((s) => s.status !== 'error').length;
};

const pillTone = (
  status: DeactivationStatusResponse | undefined
): { bg: string; ring: string; icon: React.ReactNode; label: string } => {
  const s = status?.status;
  if (s === 'completed') {
    return {
      bg: 'bg-green-50 dark:bg-green-900/20',
      ring: 'ring-green-500/40',
      icon: <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />,
      label: 'Deactivated',
    };
  }
  if (s === 'completed_with_errors') {
    return {
      bg: 'bg-amber-50 dark:bg-amber-900/20',
      ring: 'ring-amber-500/40',
      icon: <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
      label: 'Deactivated with errors',
    };
  }
  if (s === 'failed') {
    return {
      bg: 'bg-red-50 dark:bg-red-900/20',
      ring: 'ring-red-500/40',
      icon: <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />,
      label: 'Deactivation failed',
    };
  }
  return {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    ring: 'ring-blue-500/40',
    icon: <Loader2 className="w-4 h-4 text-blue-600 dark:text-blue-400 animate-spin" />,
    label: 'Deactivating',
  };
};

export const DeactivationProgressPill: React.FC = () => {
  const { active, status, isTerminal, openDrawer, dismiss } = useDeactivationTracker();

  if (!active) return null;

  const { bg, ring, icon, label } = pillTone(status);
  const done = completedStepCount(status);
  const total = DEACTIVATION_STEP_ORDER.length;

  return (
    <div className="fixed bottom-6 right-6 z-50">
      <div
        className={`flex items-center gap-3 px-3 py-2 rounded-lg shadow-lg border border-foreground/10 ring-1 ${ring} ${bg} backdrop-blur-sm`}
      >
        <button
          type="button"
          onClick={openDrawer}
          className="flex items-center gap-2 text-left"
          title="Open progress details"
        >
          {icon}
          <div className="flex flex-col">
            <span className="text-sm font-medium text-foreground leading-tight">
              {label} — {active.orgName}
            </span>
            <span className="text-xs text-muted-foreground leading-tight">
              {isTerminal ? 'Click to view details' : `${done}/${total} steps`}
            </span>
          </div>
        </button>
        {isTerminal && (
          <button
            type="button"
            onClick={dismiss}
            className="p-1 rounded hover:bg-foreground/10 text-muted-foreground"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};
